import { inject, Type } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Observable } from 'rxjs';
import { delay, finalize, map, tap } from 'rxjs/operators';
import { NavBarProvider, NavItemSource } from '../../providers/navbar';

/**
 * Configuration for the create tab navigation resolver.
 */
export interface CreateTabNavigationConfig {
  /**
   * Unique identifier for the navigation tab.
   * @example 'cases-new-case', 'investigations-new-investigation'
   */
  id: string;

  /**
   * Display title for the navigation tab.
   * @example 'New Case', 'New Investigation'
   */
  title: string;

  /**
   * URL path for the navigation tab.
   * @example '/cases/create', '/investigations/create'
   */
  anchorHref: string;
}

/**
 * Create Tab Navigation Resolver Factory.
 *
 * Creates a resolver that adds a temporary "New Case" or "New Investigation" tab
 * to the navigation bar when the user navigates to or refreshes the create page.
 *
 * This ensures the navigation tab is present even when the user directly navigates
 * to the create page via URL or browser refresh, not just when clicking the
 * "New Case" or "New Investigation" button from the dashboard.
 *
 * The tab is automatically removed when the user navigates away from the create page
 * via the page's ngOnDestroy lifecycle hook.
 *
 * @param config - Configuration object containing tab details
 * @returns ResolveFn that resolves immediately after adding the navigation tab
 *
 * @example
 * ```typescript
 * // In cases.routes.ts
 * {
 *   path: 'create',
 *   resolve: {
 *     createTab: createAddCreateTabNavigationResolver({
 *       id: 'cases-new-case',
 *       title: 'New Case',
 *       anchorHref: '/cases/create'
 *     })
 *   },
 *   loadComponent: () => import('./cases-create.page').then(m => m.CasesCreatePage)
 * }
 * ```
 */
export function createAddCreateTabNavigationResolver(
  config: CreateTabNavigationConfig
): ResolveFn<boolean> {
  return () => {
    const navBarProvider = inject(NavBarProvider);

    const navItemSource: NavItemSource = {
      id: config.id,
      title: config.title,
      anchorHref: config.anchorHref,
    };

    // Snapshot BEFORE adding: if the item is already in the permanent nav (e.g. added
    // by the parent setCasesNavigationResolver for intake specialists), we must not
    // remove it on destroy. Record that intent here, before addNavigationItem runs.
    const wasAlreadyPermanent = navBarProvider.hasNavigationItem(config.id);
    navBarProvider.addNavigationItem(navItemSource);

    // true  → tab was freshly added by this resolver → page should remove it on destroy
    // false → tab was already in the permanent nav   → page must leave it alone
    return !wasAlreadyPermanent;
  };
}

/**
 * Configuration interface for the navigation resolver factory.
 *
 * @template TPermission - The permission enum type (CasePermission, InvestigationPermission, etc.)
 */
export interface NavigationResolverConfig<TPermission> {
  /**
   * Token for the use case that fetches permissions from the API.
   * Will be injected within the resolver function context.
   */
  getPermissionsUseCaseToken: Type<{ execute(): Observable<TPermission[]> }>;

  /**
   * Token for the use case that checks if the user has a specific permission.
   * Will be injected within the resolver function context.
   */
  hasPermissionUseCaseToken: Type<{ execute(permission: TPermission): boolean }>;

  /**
   * Array of navigation item sources to filter and build into navigation items.
   */
  navItemSources: NavItemSource<TPermission>[];
}

/**
 * Navigation Resolver Factory.
 *
 * Creates a generic resolver that handles navigation setup for any module (Cases, Investigations, etc.).
 * Fetches user permissions, filters navigation items based on those permissions, and updates the navbar.
 *
 * This resolver replaces module-specific resolvers (setCasesNavigationResolver, setInvestigationsNavigationResolver)
 * with a single configurable implementation, keeping UI concerns at the routing level where they belong.
 *
 * @template TPermission - The permission enum type (CasePermission, InvestigationPermission, etc.)
 * @param config - Configuration object containing use cases and navigation sources
 * @returns ResolveFn that resolves to navigation items
 *
 * @example
 * ```typescript
 * // In route configuration
 * {
 *   path: 'cases',
 *   component: PortalShellPage,
 *   resolve: {
 *     navigation: createSetNavigationsResolver({
 *       getPermissionsUseCase: inject(GetCasePermissionsUseCase),
 *       hasPermissionUseCase: inject(HasCasePermissionUseCase),
 *       navItemSources: CASES_NAV_ITEMS
 *     })
 *   }
 * }
 * ```
 */
export function createSetNavigationsResolver<TPermission>(
  config: NavigationResolverConfig<TPermission>
): ResolveFn<NavItemSource<TPermission>[]> {
  return () => {
    const navBarProvider = inject(NavBarProvider);
    const getPermissionsUseCase = inject(config.getPermissionsUseCaseToken);
    const hasPermissionUseCase = inject(config.hasPermissionUseCaseToken);

    const { navItemSources } = config;

    const filterByPermission = (sources: NavItemSource<TPermission>[]): NavItemSource<TPermission>[] =>
       sources.filter(source => {
        if (!source.requiredPermission) return true;

        return hasPermissionUseCase.execute(source.requiredPermission);
      });

    navBarProvider.setLoadingNavigation(true);
    return getPermissionsUseCase.execute().pipe(
      delay(500),
      map(() => filterByPermission(navItemSources)),
      tap((filteredSources) => navBarProvider.updateNavigation(filteredSources)),
      finalize(() => navBarProvider.setLoadingNavigation(false))
    );
  };
}
